/**
 * Survey History Page Component for EEG Survey Integration.
 * Lists all past survey responses for the user.
 * Kept separate from existing codebase.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Calendar, CheckCircle, Clock, AlertCircle, ArrowRight, Loader, Trash2, RefreshCw, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import { surveyService } from '../../services/surveyService';
import { useAuth } from '../../contexts/AuthContext';
import './SurveyHistory.css';

const SurveyHistory = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [responses, setResponses] = useState([]);
  const [error, setError] = useState(null);
  const [redoingReports, setRedoingReports] = useState({}); // Track which reports are being redone
  const [normalizationModal, setNormalizationModal] = useState({ show: false, responseId: null, newValue: null });
  const [togglingNormalization, setTogglingNormalization] = useState({}); // Track which normalization is being toggled
  
  // Get current user email for comparison
  const getCurrentUserEmail = () => {
    if (user?.email) {
      return user.email;
    }
    try {
      const userData = localStorage.getItem(process.env.REACT_APP_USER_DATA_KEY || 'draftjam_user_data');
      return userData ? JSON.parse(userData)?.email : null;
    } catch {
      return null;
    }
  };
  
  const currentUserEmail = getCurrentUserEmail();

  // Get normalization preference for a response (default: true)
  const getNormalizationPreference = (responseId) => {
    try {
      const key = `normalization_${responseId}`;
      const stored = localStorage.getItem(key);
      return stored !== null ? stored === 'true' : true; // Default to true
    } catch {
      return true;
    }
  };

  // Set normalization preference for a response
  const setNormalizationPreference = (responseId, enabled) => {
    try {
      const key = `normalization_${responseId}`;
      localStorage.setItem(key, enabled.toString());
    } catch (error) {
      console.error('Error saving normalization preference:', error);
    }
  };

  useEffect(() => {
    loadSurveyHistory();
  }, []);

  const loadSurveyHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await surveyService.getResponses();
      setResponses(data || []);
    } catch (error) {
      console.error('Error loading survey history:', error);
      setError('Failed to load survey history');
      toast.error('Failed to load survey history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="status-icon completed" size={18} />;
      case 'in_progress':
        return <Clock className="status-icon in-progress" size={18} />;
      case 'abandoned':
        return <AlertCircle className="status-icon abandoned" size={18} />;
      default:
        return <Clock className="status-icon" size={18} />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in_progress':
        return 'In Progress';
      case 'abandoned':
        return 'Abandoned';
      default:
        return status;
    }
  };

  const handleViewResults = (responseId) => {
    navigate(`/survey/results/${responseId}`);
  };

  const handleContinueSurvey = (responseId) => {
    navigate(`/survey?responseId=${responseId}`);
  };

  const handleDeleteSurvey = async (responseId, event) => {
    event.stopPropagation(); // Prevent any parent click handlers
    
    // Confirm deletion
    const confirmed = window.confirm(
      'Are you sure you want to delete this survey? This action cannot be undone and will delete all associated data including answers and EEG readings.'
    );
    
    if (!confirmed) {
      return;
    }

    try {
      await surveyService.deleteResponse(responseId);
      toast.success('Survey deleted successfully');
      // Reload the list
      loadSurveyHistory();
    } catch (error) {
      console.error('Error deleting survey:', error);
      toast.error('Failed to delete survey');
    }
  };

  const handleRedoReport = async (responseId, event) => {
    event.stopPropagation(); // Prevent any parent click handlers
    
    // Confirm redo
    const confirmed = window.confirm(
      'This will clear all stored question interpretations and comprehensive report. They will be regenerated with updated analysis when you view the results. Continue?'
    );
    
    if (!confirmed) {
      return;
    }

    try {
      setRedoingReports(prev => ({ ...prev, [responseId]: true }));
      await surveyService.redoReport(responseId);
      toast.success('Report data cleared. Interpretations and comprehensive report will be regenerated with updated analysis when you view results.');
    } catch (error) {
      console.error('Error redoing report:', error);
      toast.error(error.response?.data?.detail || 'Failed to redo report');
    } finally {
      setRedoingReports(prev => ({ ...prev, [responseId]: false }));
    }
  };

  const handleNormalizationToggle = (responseId, event) => {
    event.stopPropagation(); // Prevent any parent click handlers
    
    const currentValue = getNormalizationPreference(responseId);
    const newValue = !currentValue;
    
    // Show modal to confirm
    setNormalizationModal({
      show: true,
      responseId: responseId,
      newValue: newValue,
      currentValue: currentValue
    });
  };

  const confirmNormalizationToggle = async () => {
    const { responseId, newValue } = normalizationModal;
    
    if (!responseId) return;

    try {
      setTogglingNormalization(prev => ({ ...prev, [responseId]: true }));
      
      // Update preference
      setNormalizationPreference(responseId, newValue);
      
      // Force regenerate report with new normalization setting
      await surveyService.generateComprehensiveReport(responseId, true, newValue);
      
      toast.success(`Normalization ${newValue ? 'enabled' : 'disabled'}. Report regenerated with updated analysis.`);
      
      // Close modal
      setNormalizationModal({ show: false, responseId: null, newValue: null });
    } catch (error) {
      console.error('Error toggling normalization:', error);
      toast.error(error.response?.data?.detail || 'Failed to toggle normalization');
    } finally {
      setTogglingNormalization(prev => ({ ...prev, [responseId]: false }));
    }
  };

  const cancelNormalizationToggle = () => {
    setNormalizationModal({ show: false, responseId: null, newValue: null });
  };

  if (loading) {
    return (
      <div className="survey-history-page">
        <div className="survey-history-container">
          <div className="survey-history-loading">
            <Loader className="spinner" />
            <p>Loading survey history...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="survey-history-page">
        <div className="survey-history-container">
          <div className="survey-history-error">
            <AlertCircle size={48} />
            <h2>Error Loading History</h2>
            <p>{error}</p>
            <button onClick={loadSurveyHistory} className="btn-retry">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-history-page">
      <div className="survey-history-container">
        <div className="survey-history-header">
          <div className="header-content">
            <Brain className="header-icon" size={32} />
            <div>
              <h1>Survey History</h1>
              <p>View and manage your past survey responses</p>
            </div>
          </div>
          <div className="header-actions">
            <button
              onClick={loadSurveyHistory}
              className="btn-refresh"
              title="Refresh survey list"
              disabled={loading}
            >
              <RefreshCw size={18} className={loading ? 'spinning' : ''} />
              Refresh
            </button>
            <button
              className="btn-new-survey"
              onClick={() => navigate('/survey')}
            >
              Start New Survey
            </button>
          </div>
        </div>

        {responses.length === 0 ? (
          <div className="survey-history-empty">
            <Brain size={64} />
            <h2>No Surveys Yet</h2>
            <p>You haven't completed any surveys yet. Start your first survey to see results here.</p>
            <button
              className="btn-primary"
              onClick={() => navigate('/survey')}
            >
              Start Your First Survey
            </button>
          </div>
        ) : (
          <div className="survey-history-list">
            {responses.map((response) => (
              <div key={response.id} className="survey-history-item">
                <div className="survey-item-header">
                  <div className="survey-item-info">
                    <h3 className="survey-item-title">
                      {response.survey_title || 'Resilience Prediction Survey'}
                    </h3>
                    <div className="survey-item-meta">
                      {response.user_email && (
                        <div className="meta-item user-email">
                          <span className="user-label">User:</span>
                          <span className="user-value">{response.user_name || response.user_email}</span>
                        </div>
                      )}
                      <div className="meta-item">
                        <Calendar size={16} />
                        <span>Started: {formatDate(response.started_at)}</span>
                      </div>
                      {response.completed_at && (
                        <div className="meta-item">
                          <CheckCircle size={16} />
                          <span>Completed: {formatDate(response.completed_at)}</span>
                        </div>
                      )}
                      <div className="meta-item">
                        <span>Answers: {response.answer_count || 0}</span>
                      </div>
                    </div>
                  </div>
                  <div className="survey-item-status">
                    {getStatusIcon(response.status)}
                    <span className="status-label">{getStatusLabel(response.status)}</span>
                  </div>
                </div>
                <div className="survey-item-actions">
                  {/* Only show delete button for own surveys */}
                  {currentUserEmail && response.user_email === currentUserEmail && (
                    <button
                      className="btn-delete"
                      onClick={(e) => handleDeleteSurvey(response.id, e)}
                      title="Delete this survey"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                  {response.status === 'completed' && (
                    <>
                      <button
                        className={`btn-normalization ${getNormalizationPreference(response.id) ? 'active' : ''}`}
                        onClick={(e) => handleNormalizationToggle(response.id, e)}
                        title={`Normalization ${getNormalizationPreference(response.id) ? 'enabled' : 'disabled'}. Click to toggle.`}
                        disabled={togglingNormalization[response.id]}
                      >
                        {togglingNormalization[response.id] ? (
                          <Loader className="spinner-small" size={18} />
                        ) : (
                          <Settings size={18} />
                        )}
                        <span className="normalization-label">
                          {getNormalizationPreference(response.id) ? 'Normalized' : 'Raw'}
                        </span>
                      </button>
                      <button
                        className="btn-redo-report"
                        onClick={(e) => handleRedoReport(response.id, e)}
                        title="Redo report with updated analysis"
                        disabled={redoingReports[response.id]}
                      >
                        {redoingReports[response.id] ? (
                          <Loader className="spinner-small" size={18} />
                        ) : (
                          <RefreshCw size={18} />
                        )}
                        {redoingReports[response.id] ? 'Redoing...' : 'Redo Report'}
                      </button>
                    </>
                  )}
                  {response.status === 'completed' ? (
                    <button
                      className="btn-view-results"
                      onClick={() => handleViewResults(response.id)}
                    >
                      View Results
                      <ArrowRight size={18} />
                    </button>
                  ) : (
                    <button
                      className="btn-continue"
                      onClick={() => handleContinueSurvey(response.id)}
                    >
                      Continue Survey
                      <ArrowRight size={18} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Normalization Toggle Modal */}
      {normalizationModal.show && (
        <div className="modal-overlay" onClick={cancelNormalizationToggle}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Toggle Normalization?</h3>
            </div>
            <div className="modal-body">
              <p>
                Changing normalization will regenerate the comprehensive report with{' '}
                <strong>{normalizationModal.newValue ? 'normalized' : 'raw'}</strong> EEG readings.
              </p>
              <p>
                This will replace the current report and may take a few moments. Continue?
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={cancelNormalizationToggle}
                disabled={togglingNormalization[normalizationModal.responseId]}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={confirmNormalizationToggle}
                disabled={togglingNormalization[normalizationModal.responseId]}
              >
                {togglingNormalization[normalizationModal.responseId] ? (
                  <>
                    <Loader className="spinner-small" size={16} />
                    Regenerating...
                  </>
                ) : (
                  'Yes, Regenerate Report'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyHistory;
